import type { Ref } from "react";
import type { ProductGroup } from "../lib/productGroups";
import ProductCard from "./ProductCard";

// Flexbox instead of CSS Grid: grid-template-columns always reserves N full
// tracks even when fewer cards exist to fill them, which either stretches a
// sparse row's cards to fill the leftover tracks or (if capped) leaves the
// container's shared border spanning empty space. Flex-wrap only lays out
// the cards that actually exist, so a card's width is always its normal
// fraction of the row — matching every other row's column width exactly —
// and the border never extends past the last real card.
const COLUMN_WIDTHS: Record<"2-3" | "2-3-4" | "1-2-3" | "2-4" | "1-4", string> = {
  "2-3": "w-1/2 md:w-1/3",
  "2-3-4": "w-1/2 sm:w-1/3 xl:w-1/4",
  "1-2-3": "w-full sm:w-1/2 xl:w-1/3",
  "2-4": "w-1/2 md:w-1/3 2xl:w-1/4",
  // One full-width card per row (stacked, 4 rows) below lg, exactly 4 across
  // at lg+ — used for a fixed-count row like "similar items" rather than a
  // catalog grid that reflows with however many results exist.
  "1-4": "w-full lg:w-1/4",
};

export default function ProductGrid({
  groups,
  columns,
  className = "",
  gridRef,
}: {
  groups: ProductGroup[];
  columns: keyof typeof COLUMN_WIDTHS;
  className?: string;
  gridRef?: Ref<HTMLDivElement>;
}) {
  return (
    <div ref={gridRef} className={`flex flex-wrap pl-px pt-px ${className}`}>
      {groups.map((group, index) => (
        // Every card supplies its own full border (see ProductCard); the
        // -ml-px/-mt-px here pulls each card 1px up-left so its border
        // overlaps the previous card's border into a single shared line
        // instead of doubling. The container's pl-px/pt-px above compensates
        // so the first row/column isn't shifted outside its own box.
        <div key={group.key} className={`-ml-px -mt-px ${COLUMN_WIDTHS[columns]}`}>
          {/* First row's cards load eagerly at high fetch priority — one of
              them is reliably the page's LCP element (Cloudflare flagged
              ~3.5-3.9s LCP on catalog pages), and every card image was
              unconditionally loading="lazy" before this, deprioritizing the
              fetch of an image already visible on initial paint. 4 covers
              the widest column count this grid uses (COLUMN_WIDTHS' "2-4"
              breakpoint), so it's never too few even on a narrow row. */}
          <ProductCard group={group} priority={index < 4} />
        </div>
      ))}
    </div>
  );
}
